import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cacheUtils, getOptimizedImageUrl, createImageWithFallback } from '../utils/cacheUtils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  quality?: number;
  fallback?: string;
  lazy?: boolean;
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
  style?: React.CSSProperties;
  id?: string;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  width,
  height,
  quality = 80,
  fallback = '/assets/img/avatar.png',
  lazy = true,
  placeholder,
  onLoad,
  onError,
  style,
  id,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(placeholder || '');
  const [isInView, setIsInView] = useState(!lazy);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const optimizedSrc = getOptimizedImageUrl(src, { width, height, quality });
  const cacheKey = `img-${optimizedSrc}`;

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
    cacheUtils.images.set(cacheKey, { loaded: true, src: optimizedSrc });
    onLoad?.();
  }, [cacheKey, optimizedSrc, onLoad]);

  const handleImageError = useCallback(() => {
    setImageError(true);
    if (fallback && fallback !== optimizedSrc) {
      createImageWithFallback(fallback)
        .then(() => {
          setImageSrc(fallback);
          setImageError(false);
        })
        .catch(() => {
          setImageError(true);
          onError?.();
        });
    } else {
      onError?.();
    }
  }, [fallback, optimizedSrc, onError]);

  const loadImage = useCallback(async () => {
    if (!optimizedSrc || imageLoaded) return;

    const cached = cacheUtils.images.get(cacheKey);
    if (cached?.loaded) {
      setImageSrc(optimizedSrc);
      setImageLoaded(true);
      return;
    }

    try {
      await createImageWithFallback(optimizedSrc, fallback);
      setImageSrc(optimizedSrc);
      handleImageLoad();
    } catch (error) {
      handleImageError();
    }
  }, [optimizedSrc, imageLoaded, cacheKey, fallback, handleImageLoad, handleImageError]);

  useEffect(() => {
    if (!lazy) {
      loadImage();
      return;
    }

    if (!imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [lazy, loadImage]);

  useEffect(() => {
    if (isInView) {
      loadImage();
    }
  }, [isInView, loadImage]);

  const imgStyle: React.CSSProperties = {
    ...style,
    opacity: imageLoaded ? 1 : 0.7,
    transition: 'opacity 0.3s ease-in-out',
  };

  if (imageError && !fallback) {
    return (
      <div 
        className={`${className} image-placeholder`} 
        style={{
          width: width || 'auto',
          height: height || 'auto',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '12px',
          ...style,
        }}
      >
        Image not available
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={imageSrc || placeholder || optimizedSrc}
      alt={alt}
      className={className}
      width={width}
      height={height}
      style={imgStyle}
      id={id}
      onLoad={handleImageLoad}
      onError={handleImageError}
      loading={lazy ? 'lazy' : 'eager'}
    />
  );
};

export default OptimizedImage; 